import { useState, useEffect } from "react";
import { getExpenses, approveExpense, reimburseExpense, getUsers, bulkActionExpenses } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  CheckCircle2,
  XCircle,
  Wallet,
  Eye,
  Search,
  Clock,
  FileText,
  Users,
  CheckCheck,
  X
} from "lucide-react";
import { Link } from "react-router-dom";

export default function Approvals() {
  const { user, hasRole } = useAuth();
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [approvedExpenses, setApprovedExpenses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(false);

  // Sélection multiple
  const [selectedPending, setSelectedPending] = useState(new Set());
  const [selectedApproved, setSelectedApproved] = useState(new Set());
  const [bulkRejectDialogOpen, setBulkRejectDialogOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");

  useEffect(() => {
    fetchData();
  }, [userFilter]);

  useEffect(() => {
    getUsers().then(res => setUsers(res.data)).catch(() => {});
  }, []);

  // Réinitialiser les sélections quand les données changent
  useEffect(() => { setSelectedPending(new Set()); }, [pendingExpenses]);
  useEffect(() => { setSelectedApproved(new Set()); }, [approvedExpenses]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = userFilter !== "all" ? { user_id: userFilter } : {};
      const [pendingRes, approvedRes] = await Promise.all([
        getExpenses({ ...params, status: "submitted" }),
        getExpenses({ ...params, status: "approved" })
      ]);
      setPendingExpenses(pendingRes.data);
      setApprovedExpenses(approvedRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (expense) => {
    setProcessing(true);
    try {
      await approveExpense(expense.id, { action: "approve" });
      toast.success(`Dépense de ${expense.user_name} approuvée`);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'approbation");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedExpense) return;
    setProcessing(true);
    try {
      await approveExpense(selectedExpense.id, { action: "reject", reason: rejectReason });
      toast.success("Dépense rejetée");
      setRejectDialogOpen(false);
      setRejectReason("");
      setSelectedExpense(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors du rejet");
    } finally {
      setProcessing(false);
    }
  };

  const handleReimburse = async (expense) => {
    setProcessing(true);
    try {
      await reimburseExpense(expense.id);
      toast.success(`Dépense de ${expense.user_name} marquée comme remboursée`);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors du remboursement");
    } finally {
      setProcessing(false);
    }
  };

  // Actions en masse
  const handleBulkApprove = async () => {
    if (selectedPending.size === 0) return;
    setProcessing(true);
    try {
      const res = await bulkActionExpenses({ expense_ids: [...selectedPending], action: "approve" });
      const { processed, errors } = res.data;
      toast.success(`${processed} dépense(s) approuvée(s)${errors > 0 ? `, ${errors} erreur(s)` : ""}`);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'approbation en masse");
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedPending.size === 0) return;
    setProcessing(true);
    try {
      const res = await bulkActionExpenses({ expense_ids: [...selectedPending], action: "reject", reason: bulkRejectReason });
      const { processed, errors } = res.data;
      toast.success(`${processed} dépense(s) refusée(s)${errors > 0 ? `, ${errors} erreur(s)` : ""}`);
      setBulkRejectDialogOpen(false);
      setBulkRejectReason("");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors du refus en masse");
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkReimburse = async () => {
    if (selectedApproved.size === 0) return;
    setProcessing(true);
    try {
      const res = await bulkActionExpenses({ expense_ids: [...selectedApproved], action: "reimburse" });
      const { processed, errors } = res.data;
      toast.success(`${processed} dépense(s) remboursée(s)${errors > 0 ? `, ${errors} erreur(s)` : ""}`);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors du remboursement en masse");
    } finally {
      setProcessing(false);
    }
  };

  // Helpers sélection
  const togglePending = (id) => {
    setSelectedPending(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllPending = (items) => {
    if (selectedPending.size === items.length) {
      setSelectedPending(new Set());
    } else {
      setSelectedPending(new Set(items.map(e => e.id)));
    }
  };

  const toggleApproved = (id) => {
    setSelectedApproved(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllApproved = (items) => {
    if (selectedApproved.size === items.length) {
      setSelectedApproved(new Set());
    } else {
      setSelectedApproved(new Set(items.map(e => e.id)));
    }
  };

  const formatCurrency = (amount, currency = "EUR") => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount);
  };

  const filteredPending = pendingExpenses.filter(expense => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      expense.description.toLowerCase().includes(term) ||
      expense.user_name.toLowerCase().includes(term) ||
      expense.category_name?.toLowerCase().includes(term)
    );
  });

  const filteredApproved = approvedExpenses.filter(expense => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      expense.description.toLowerCase().includes(term) ||
      expense.user_name.toLowerCase().includes(term) ||
      expense.category_name?.toLowerCase().includes(term)
    );
  });

  const pendingAllSelected = filteredPending.length > 0 && selectedPending.size === filteredPending.length;
  const pendingIndeterminate = selectedPending.size > 0 && selectedPending.size < filteredPending.length;
  const approvedAllSelected = filteredApproved.length > 0 && selectedApproved.size === filteredApproved.length;
  const approvedIndeterminate = selectedApproved.size > 0 && selectedApproved.size < filteredApproved.length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="approvals-page">
      {/* Header */}
      <div>
        <h1 className="page-title">Validations</h1>
        <p className="page-description">Gérez les demandes de remboursement</p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par collaborateur, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            {users.length > 0 && (
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-full sm:w-[220px]" data-testid="user-filter">
                  <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Collaborateur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les collaborateurs</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" className="gap-2" data-testid="pending-tab">
            <Clock className="h-4 w-4" />
            En attente ({pendingExpenses.length})
          </TabsTrigger>
          {hasRole(["accountant", "admin"]) && (
            <TabsTrigger value="approved" className="gap-2" data-testid="approved-tab">
              <CheckCircle2 className="h-4 w-4" />
              À rembourser ({approvedExpenses.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab En attente */}
        <TabsContent value="pending" className="mt-6 space-y-3">
          {/* Barre d'actions en masse */}
          {selectedPending.size > 0 && (
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg border">
              <span className="text-sm font-medium text-muted-foreground">
                {selectedPending.size} sélectionnée(s)
              </span>
              <div className="flex gap-2 ml-auto">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                  onClick={handleBulkApprove}
                  disabled={processing}
                  data-testid="bulk-approve-btn"
                >
                  <CheckCheck className="h-4 w-4 mr-1" />
                  Tout approuver
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  onClick={() => setBulkRejectDialogOpen(true)}
                  disabled={processing}
                  data-testid="bulk-reject-btn"
                >
                  <X className="h-4 w-4 mr-1" />
                  Tout refuser
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedPending(new Set())}
                >
                  Annuler
                </Button>
              </div>
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : filteredPending.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={pendingAllSelected}
                          data-state={pendingIndeterminate ? "indeterminate" : undefined}
                          onCheckedChange={() => toggleAllPending(filteredPending)}
                          aria-label="Tout sélectionner"
                          data-testid="select-all-pending"
                        />
                      </TableHead>
                      <TableHead>Collaborateur</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPending.map((expense) => (
                      <TableRow
                        key={expense.id}
                        data-testid={`pending-row-${expense.id}`}
                        className={selectedPending.has(expense.id) ? "bg-muted/50" : ""}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedPending.has(expense.id)}
                            onCheckedChange={() => togglePending(expense.id)}
                            aria-label={`Sélectionner ${expense.description}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{expense.user_name}</TableCell>
                        <TableCell>
                          <div>
                            <p>{expense.description}</p>
                            {expense.project && (
                              <p className="text-xs text-muted-foreground">{expense.project}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{expense.date}</TableCell>
                        <TableCell>{expense.category_name}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          {formatCurrency(expense.amount, expense.currency)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Link to={`/expenses/${expense.id}`}>
                              <Button variant="ghost" size="icon" data-testid={`view-${expense.id}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                            </Link>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => handleApprove(expense)}
                              disabled={processing}
                              data-testid={`approve-${expense.id}`}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Approuver
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                setSelectedExpense(expense);
                                setRejectDialogOpen(true);
                              }}
                              disabled={processing}
                              data-testid={`reject-${expense.id}`}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Refuser
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <p className="text-muted-foreground">Aucune dépense en attente de validation</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab À rembourser */}
        {hasRole(["accountant", "admin"]) && (
          <TabsContent value="approved" className="mt-6 space-y-3">
            {/* Barre d'actions en masse */}
            {selectedApproved.size > 0 && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg border">
                <span className="text-sm font-medium text-muted-foreground">
                  {selectedApproved.size} sélectionnée(s)
                </span>
                <div className="flex gap-2 ml-auto">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200"
                    onClick={handleBulkReimburse}
                    disabled={processing}
                    data-testid="bulk-reimburse-btn"
                  >
                    <Wallet className="h-4 w-4 mr-1" />
                    Tout rembourser
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedApproved(new Set())}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}

            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-6 space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : filteredApproved.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={approvedAllSelected}
                            data-state={approvedIndeterminate ? "indeterminate" : undefined}
                            onCheckedChange={() => toggleAllApproved(filteredApproved)}
                            aria-label="Tout sélectionner"
                            data-testid="select-all-approved"
                          />
                        </TableHead>
                        <TableHead>Collaborateur</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Approuvé le</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredApproved.map((expense) => (
                        <TableRow
                          key={expense.id}
                          data-testid={`approved-row-${expense.id}`}
                          className={selectedApproved.has(expense.id) ? "bg-muted/50" : ""}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedApproved.has(expense.id)}
                              onCheckedChange={() => toggleApproved(expense.id)}
                              aria-label={`Sélectionner ${expense.description}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{expense.user_name}</TableCell>
                          <TableCell>{expense.description}</TableCell>
                          <TableCell>
                            {expense.approved_at && format(new Date(expense.approved_at), "PPP", { locale: fr })}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {formatCurrency(expense.amount, expense.currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              onClick={() => handleReimburse(expense)}
                              disabled={processing}
                              data-testid={`reimburse-${expense.id}`}
                            >
                              <Wallet className="h-4 w-4 mr-1" />
                              Rembourser
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12">
                    <Wallet className="h-12 w-12 mx-auto text-purple-500 mb-4" />
                    <p className="text-muted-foreground">Aucune dépense à rembourser</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Dialog refus individuel */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refuser la dépense</DialogTitle>
            <DialogDescription>
              Veuillez indiquer la raison du refus. Cette information sera visible par le collaborateur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedExpense && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{selectedExpense.description}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedExpense.user_name} • {formatCurrency(selectedExpense.amount)}
                </p>
              </div>
            )}
            <Textarea
              placeholder="Raison du refus..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              data-testid="reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={processing || !rejectReason.trim()}
              data-testid="confirm-reject"
            >
              Refuser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog refus en masse */}
      <Dialog open={bulkRejectDialogOpen} onOpenChange={setBulkRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refuser {selectedPending.size} dépense(s)</DialogTitle>
            <DialogDescription>
              Cette raison sera appliquée à toutes les dépenses sélectionnées.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Raison du refus..."
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              rows={3}
              data-testid="bulk-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRejectDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkReject}
              disabled={processing || !bulkRejectReason.trim()}
              data-testid="confirm-bulk-reject"
            >
              Refuser {selectedPending.size} dépense(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
